import React, { useEffect } from 'react';
import {
  GreenhouseJob,
  ResumeScreeningConfig,
  GreenhouseActionType,
  GreenhouseAction,
  NextResumeScreeningAction,
  ScreenType,
} from '@scale/shared-types';
import {
  CreateResumeScreeningSteps,
  ResumeScreeningAction,
  actionTypeMappping,
} from '../constants';
import { useQueryClient } from 'react-query';
import { ImportGreenhouseJob } from './ImportGreenhouseJobSlide';
import { CreateAssistant } from './CreateAssistantSlide';
import { ScreenActions } from './ScreenActionsSlide';

const fetchAllChainedJobConfigs = (
  rootConfig: ResumeScreeningConfig,
  allJobConfigs: ResumeScreeningConfig[],
) => {
  const configsToUpdate: ResumeScreeningConfig[] = [];
  let currentConfig: ResumeScreeningConfig | undefined = rootConfig;

  while (currentConfig) {
    configsToUpdate.push(currentConfig);
    const nextScreenAction = currentConfig.onFailActions.find(
      action => action.type === GreenhouseActionType.NextResumeScreening,
    ) as NextResumeScreeningAction;

    const nextConfigId = nextScreenAction?.nextResumeScreeningConfigId;
    currentConfig = nextConfigId
      ? allJobConfigs.find(jobConfig => jobConfig._id === nextConfigId)
      : undefined;
  }

  return configsToUpdate;
};

export const ResumeScreenDetailPanel: React.FC<{
  selectedJobConfig: ResumeScreeningConfig;
  jobStages: Record<string, string>;
  loadingJobStages: boolean;
  greenhouseJobs: GreenhouseJob[];
  loadingGreenhouseJobs: boolean;
  allJobConfigs: ResumeScreeningConfig[];
  openJobConfigTestDialog: (jobConfig: ResumeScreeningConfig) => void;
  activeDetailPanel: CreateResumeScreeningSteps;
  activeJobConfig: ResumeScreeningConfig;
  setActiveJobConfig: (jobConfig: ResumeScreeningConfig) => void;
  setLoadingFlowDiagram: (loading: boolean) => void;
  isRecruitingScreeningEnabled: boolean;
}> = ({
  selectedJobConfig,
  jobStages,
  loadingJobStages,
  greenhouseJobs,
  loadingGreenhouseJobs,
  allJobConfigs,
  openJobConfigTestDialog,
  activeDetailPanel,
  activeJobConfig,
  setActiveJobConfig,
  isRecruitingScreeningEnabled,
}) => {
  const queryClient = useQueryClient();
  const [isImportGreenhouseJobOpen, setIsImportGreenhouseJobOpen] = React.useState<boolean>(true);
  const [isCreateAssistantOpen, setIsCreateAssistantOpen] = React.useState<boolean>(true);
  const [validChainableConfigs, setValidChainableConfigs] = React.useState<ResumeScreeningConfig[]>(
    [],
  ); // Valid configs that can be chained without creating a cycle

  useEffect(() => {
    const fetchValidChainableConfigs = async () => {
      const rootConfig = allJobConfigs.find(config => config._id === selectedJobConfig._id); // Needed since local updates are reflected in allJobConfigs
      if (!rootConfig) return;
      const chainedConfigs = await fetchAllChainedJobConfigs(rootConfig, allJobConfigs);
      const updatedValidChainableConfigs = allJobConfigs.filter(
        config =>
          config.greenhouseJobId === selectedJobConfig.greenhouseJobId &&
          !chainedConfigs.includes(config),
      );
      setValidChainableConfigs(updatedValidChainableConfigs);
    };

    fetchValidChainableConfigs();
  }, [selectedJobConfig, allJobConfigs]);

  const updateAllJobConfigs = (updatedConfig: ResumeScreeningConfig) => {
    allJobConfigs = allJobConfigs.map(jobConfig => {
      if (jobConfig._id === updatedConfig._id) {
        return updatedConfig;
      }
      return jobConfig;
    });
    queryClient.setQueryData(['listJobConfigs', isRecruitingScreeningEnabled], allJobConfigs);
  };

  const handleAddQuestion = () => {
    const updatedQuestions = [...(activeJobConfig?.questionsToAskGPT || []), ''];
    const updatedConfig = {
      ...activeJobConfig,
      questionsToAskGPT: updatedQuestions,
      tested: false,
    };
    setActiveJobConfig(updatedConfig);
    updateAllJobConfigs(updatedConfig);
  };

  const handleRemoveQuestion = (index: number) => {
    const updatedQuestions = [...(activeJobConfig?.questionsToAskGPT || [])];
    updatedQuestions.splice(index, 1);
    const updatedConfig = {
      ...activeJobConfig,
      questionsToAskGPT: updatedQuestions,
      tested: false,
    };
    setActiveJobConfig(updatedConfig);
    updateAllJobConfigs(updatedConfig);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>,
    index?: number,
  ) => {
    const { name, value } = e.target;
    if (name === 'questionsToAskGPT' && index !== undefined) {
      const updatedQuestions = [...(activeJobConfig?.questionsToAskGPT || [])];
      updatedQuestions[index] = value;
      const updatedConfig = {
        ...activeJobConfig,
        questionsToAskGPT: updatedQuestions,
        tested: false,
      };
      setActiveJobConfig(updatedConfig);
      updateAllJobConfigs(updatedConfig);
    } else {
      const updatedConfig = { ...activeJobConfig, [name]: value, tested: false };
      setActiveJobConfig(updatedConfig);
      updateAllJobConfigs(updatedConfig);
    }
  };

  const handleRequiresAllQuestionsPassedChange = (requiresAllQuestionsPassed: boolean) => {
    const updatedConfig = { ...activeJobConfig, requiresAllQuestionsPassed };
    setActiveJobConfig(updatedConfig);
    updateAllJobConfigs(updatedConfig);
  };

  const handleScreenTypeChange = (screenType: ScreenType) => {
    const updatedConfig = { ...activeJobConfig, type: screenType };
    if (screenType === ScreenType.OCP) {
      updatedConfig.greenhouseJobId = 'ocp-job-id'; // todo: this is a placeholder for OCP. once GH resume screens are deprecated, this field will be removed
      updatedConfig.greenhouseJobName = 'ocp-job-name'; // placeholder for OCP, will be removed once GH resume screens are deprecated
      updatedConfig.resumeReviewStageId = 'ocp-stage-id'; // placeholder for OCP, will be removed once GH resume screens are deprecated
      updatedConfig.onPassActions = [];
      updatedConfig.onFailActions = [];
      updatedConfig.onUnsupportedFileActions = [];
    }
    setActiveJobConfig(updatedConfig);
    updateAllJobConfigs(updatedConfig);
  };

  // actionCategory is one of Pass, Fail, NeedsHumanReview. actionType is one of MoveStage, EditTags, NextResumeScreening
  const handleAddAction = (
    actionCategory: ResumeScreeningAction,
    actionType: GreenhouseActionType,
    newAction: GreenhouseAction,
  ) => {
    const actions: GreenhouseAction[] =
      (activeJobConfig[actionTypeMappping[actionCategory]] as GreenhouseAction[]) || [];
    let updatedActions: GreenhouseAction[];
    if (actions.find(action => action.type === actionType)) {
      updatedActions = actions.map(action => {
        if (action.type === actionType) {
          return newAction;
        }
        return action;
      });
    } else {
      updatedActions = [...actions, newAction];
    }

    const updatedConfig = {
      ...activeJobConfig,
      [actionTypeMappping[actionCategory]]: updatedActions,
    };
    setActiveJobConfig(updatedConfig);
    updateAllJobConfigs(updatedConfig);
  };

  const handleRemoveAction = (
    actionCategory: ResumeScreeningAction,
    actionType: GreenhouseActionType,
  ) => {
    const actions: GreenhouseAction[] = activeJobConfig[
      actionTypeMappping[actionCategory]
    ] as GreenhouseAction[];
    const updatedActions = actions?.filter(action => action.type !== actionType);
    const updatedConfig = {
      ...activeJobConfig,
      [actionTypeMappping[actionCategory]]: updatedActions,
    };
    setActiveJobConfig(updatedConfig);
    updateAllJobConfigs(updatedConfig);
  };

  return (
    <div className="flex flex-col p-4 overflow-y-auto h-full">
      {activeDetailPanel === CreateResumeScreeningSteps.ImportGreenhouseJob && (
        <div>
          <ImportGreenhouseJob
            selectedJobConfig={activeJobConfig}
            setSelectedJobConfig={setActiveJobConfig}
            greenhouseJobs={greenhouseJobs}
            loadingGreenhouseJobs={loadingGreenhouseJobs}
            jobStages={jobStages}
            loadingJobStages={loadingJobStages}
            handleInputChange={handleInputChange}
            handleScreenTypeChange={handleScreenTypeChange}
            updateAllJobConfigs={updateAllJobConfigs}
            isImportGreenhouseJobOpen={isImportGreenhouseJobOpen}
            toggleImportGreenhouseJobOpen={() => {
              setIsImportGreenhouseJobOpen(!isImportGreenhouseJobOpen);
            }}
            isRecruitingScreeningEnabled={isRecruitingScreeningEnabled}
          />
          <CreateAssistant
            selectedJobConfig={activeJobConfig}
            handleInputChange={handleInputChange}
            handleAddQuestion={handleAddQuestion}
            handleRemoveQuestion={handleRemoveQuestion}
            openJobConfigTestDialog={openJobConfigTestDialog}
            isCreateAssistantOpen={isCreateAssistantOpen}
            toggleCreateAssistantOpen={() => {
              setIsCreateAssistantOpen(!isCreateAssistantOpen);
            }}
            handleRequiresAllQuestionsPassedChange={handleRequiresAllQuestionsPassedChange}
          />
        </div>
      )}
      {activeDetailPanel === CreateResumeScreeningSteps.PassActions && (
        <ScreenActions
          actionType={ResumeScreeningAction.Pass}
          selectedJobConfig={activeJobConfig}
          jobStages={jobStages}
          loadingJobStages={loadingJobStages}
          handleActionUpdate={handleAddAction}
          handleRemoveAction={handleRemoveAction}
          allJobConfigs={allJobConfigs}
          validChainableConfigs={validChainableConfigs}
        />
      )}
      {activeDetailPanel === CreateResumeScreeningSteps.NeedsHumanReviewActions && (
        <ScreenActions
          actionType={ResumeScreeningAction.NeedsHumanReview}
          selectedJobConfig={activeJobConfig}
          jobStages={jobStages}
          loadingJobStages={loadingJobStages}
          handleActionUpdate={handleAddAction}
          handleRemoveAction={handleRemoveAction}
          allJobConfigs={allJobConfigs}
          validChainableConfigs={validChainableConfigs}
        />
      )}
      {activeDetailPanel === CreateResumeScreeningSteps.FailActions && (
        <ScreenActions
          actionType={ResumeScreeningAction.Fail}
          selectedJobConfig={activeJobConfig}
          jobStages={jobStages}
          loadingJobStages={loadingJobStages}
          handleActionUpdate={handleAddAction}
          handleRemoveAction={handleRemoveAction}
          allJobConfigs={allJobConfigs}
          validChainableConfigs={validChainableConfigs}
        />
      )}
    </div>
  );
};
